import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button
} from '@mui/material';

import {
    Weekend,
    Kitchen,
    Tv,
    Chair,
    CheckCircle,
    AttachMoney,
    AssignmentInd,
    Group,
    Home,
    AccountTree,
    DoneAll,
    ShoppingCart,
    LocalLaundryService,
    WbSunny,
    LocalDining,
    Lock,
    Delete,
    SwapHoriz,
    AccountBalanceWallet,
    DevicesOther,
    ArrowRightAlt,
    Menu,
    Close
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: 'はじめに（ルームシェアの家具・家電選びの重要性）' },
    { id: 'furniture-list', label: 'ルームシェアに必要な家具・家電20選' },
    { id: 'space-efficient', label: 'スペース効率を高める選び方のコツ' },
    { id: 'durability-usability', label: '耐久性と使いやすさを考える' },
    { id: 'cost-sharing', label: 'コスト分担のおすすめ方法' },
    { id: 'ownership', label: '所有権の明確化とトラブル防止策' },
    { id: 'conclusion', label: 'まとめと快適ルームシェアのヒント' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)} sx={{ ml: 2 }}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32,
                                        minHeight: 32,
                                        bgcolor: '#f5f5f5',
                                        color: '#888',
                                        fontWeight: 'bold',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 1,
                                        fontSize: '1.1rem'
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.97rem', textAlign: 'left' }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                    startIcon={<Menu />}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入部分 */}
            <Box id="introduction" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    はじめに（ルームシェアの家具・家電選びの重要性）
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    近年、生活コストの最適化や新しい人間関係の広がりを求めて、ルームシェアを選ぶ人が増えています。ですが、共同生活を快適にスタートするためには、家具や家電の選び方がとても重要。<b>「何を揃えればいいの？」「どうやって分担・管理するの？」</b>と悩む方も多いはずです。
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    本記事では、<Chip label="ルームシェア" color="primary" variant="outlined" size="small" sx={{ mx: 0.5 }} />のための家具・家電選びについて、<b>必須リスト・選び方のコツ・トラブル予防策</b>まで、専門的な視点で分かりやすく解説します。
                </Typography>
                <Paper elevation={0} sx={{ my: 3, p: 2, bgcolor: '#f9fbe7', borderLeft: '5px solid #1976d2' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#388e3c' }}>
                        この記事でわかること
                    </Typography>
                    <List dense>
                        <ListItem sx={{ py: 0 }}>
                            <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                            <ListItemText primary="ルームシェアに必要な家具・家電の全リスト" />
                        </ListItem>
                        <ListItem sx={{ py: 0 }}>
                            <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                            <ListItemText primary="スペース効率・耐久性を考えた選び方" />
                        </ListItem>
                        <ListItem sx={{ py: 0 }}>
                            <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                            <ListItemText primary="費用分担や所有権のベストプラクティス" />
                        </ListItem>
                    </List>
                </Paper>
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                    <img
                        src="/images/blog/roomshare-living_roomshare-furniture-appliance-20-items.jpg"
                        alt="明るい共有リビングでルームメイト同士がくつろぎながら会話している様子。大型ソファと観葉植物、ナチュラル系インテリアが居心地の良い雰囲気を演出し、共同生活の温かさと快適さを伝えている"
                        style={{ maxWidth: '100%', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                    />
                </Box>
            </Box>


            {/* 必要な家具・家電リスト */}
            <Box id="furniture-list" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアに必要な家具・家電20選
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアで「本当に必要なもの」を押さえることが、快適な共同生活の第一歩。<b>共用スペース用・個室用・あると便利なアイテム</b>に分けて、厳選20アイテムを解説します。
                </Typography>
                <Grid container spacing={2} sx={{ mt: 2 }}>
                    {/* 共用スペース用 */}
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, minHeight: 380 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <Home sx={{ verticalAlign: 'middle', mr: 1 }} />共用スペース用
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><Weekend color="primary" /></ListItemIcon>
                                    <ListItemText primary="ソファ・リビングテーブル" secondary="みんなが集まる空間の中心。座り心地や掃除のしやすさ重視。" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Kitchen color="secondary" /></ListItemIcon>
                                    <ListItemText primary="冷蔵庫（大型・冷凍庫付）" secondary="食材や飲み物を共用で保存。棚やボックスで管理を明確に。" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Tv color="action" /></ListItemIcon>
                                    <ListItemText primary="テレビ・AV機器" secondary="リラックスタイムに欠かせない。音量や利用ルールの共有も大切。" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><LocalDining color="primary" /></ListItemIcon>
                                    <ListItemText primary="ダイニングテーブル＆チェア" secondary="食事や作業に使える多目的タイプがおすすめ。" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><DevicesOther color="action" /></ListItemIcon>
                                    <ListItemText primary="電子レンジ・電気ケトル" secondary="忙しい朝や夜食に便利。複数人分の容量を意識。" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><LocalLaundryService color="secondary" /></ListItemIcon>
                                    <ListItemText primary="洗濯機・乾燥機" secondary="共用だからこそ耐久性や容量が重要。スケジュール管理もポイント。" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><WbSunny color="primary" /></ListItemIcon>
                                    <ListItemText primary="掃除機" secondary="コードレス・静音モデルが好評。共用スペースの清潔を保つ必須アイテム。" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Chair color="action" /></ListItemIcon>
                                    <ListItemText primary="シューズラック・玄関収納" secondary="玄関まわりをすっきり。靴や傘のスペースを分けられるタイプが便利。" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><ShoppingCart color="primary" /></ListItemIcon>
                                    <ListItemText primary="キッチン収納・食器棚" secondary="食器や調味料の置き場を明確化。引き出し式や可動棚が便利。" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Lock color="action" /></ListItemIcon>
                                    <ListItemText primary="貴重品ロッカー・鍵付き収納" secondary="プライバシーや安全対策に。" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    {/* 個室用 */}
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, minHeight: 380 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <AssignmentInd sx={{ verticalAlign: 'middle', mr: 1 }} />個室用
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><Chair color="primary" /></ListItemIcon>
                                    <ListItemText primary="ベッド・寝具" secondary="快眠のためのマットレス選びが重要。収納付きベッドもおすすめ。" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AccountTree color="secondary" /></ListItemIcon>
                                    <ListItemText primary="デスク＆チェア" secondary="テレワークや勉強に必須。コンパクトで機能的なものを。" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Delete color="action" /></ListItemIcon>
                                    <ListItemText primary="ゴミ箱（個人用）" secondary="部屋の清潔維持に。ニオイ漏れしにくい蓋付きモデル。" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><SwapHoriz color="primary" /></ListItemIcon>
                                    <ListItemText primary="収納ボックス・衣装ケース" secondary="衣類や私物の整理整頓に欠かせない。" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><DoneAll color="secondary" /></ListItemIcon>
                                    <ListItemText primary="延長コード・電源タップ" secondary="スマホ・PC・家電の同時充電に。" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    {/* あると便利な＋α */}
                    <Grid item xs={12} sm={12}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mt: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <Group sx={{ verticalAlign: 'middle', mr: 1 }} />あると快適＋α
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><Tv color="primary" /></ListItemIcon>
                                    <ListItemText primary="サブモニター・プロジェクター" secondary="映画鑑賞やオンライン会議に活躍。" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AccountBalanceWallet color="secondary" /></ListItemIcon>
                                    <ListItemText primary="小型金庫・セキュリティボックス" secondary="現金や重要書類の管理に安心。" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><WbSunny color="action" /></ListItemIcon>
                                    <ListItemText primary="空気清浄機・加湿器" secondary="花粉や乾燥対策に。" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><LocalLaundryService color="primary" /></ListItemIcon>
                                    <ListItemText primary="アイロン・衣類スチーマー" secondary="ビジネスや就活で活躍する便利アイテム。" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Lock color="secondary" /></ListItemIcon>
                                    <ListItemText primary="耳栓・アイマスク" secondary="生活リズムの違いによるストレスを軽減。" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <img
                        src="/images/blog/shared-kitchen_roomshare-furniture-appliance-20-items.jpg"
                        alt="複数人で利用する広いキッチンで、ルームメイトたちが一緒に料理をしている様子。明るく清潔感のある空間に大きな冷蔵庫や収納棚が並び、共同生活の利便性と温かさを感じさせる"
                        style={{ maxWidth: '100%', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                    />
                </Box>
            </Box>


            {/* スペース効率の良い選び方 */}
            <Box id="space-efficient" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    スペース効率を高める選び方のコツ
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアでは、<b>限られたスペースを最大限に活かす</b>ことがとても大切。アイテム選びの際は、<Chip label="多機能" color="primary" size="small" sx={{ mx: 0.5 }} /><Chip label="省スペース" color="primary" size="small" />を意識しましょう。
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: '#f3e5f5', borderRadius: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                <ArrowRightAlt sx={{ verticalAlign: 'middle', mr: 1 }} />選び方のポイント
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircle color="primary" fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="スタッキング・折りたたみ式家具"
                                        secondary="使わない時は重ねて収納。来客時も柔軟に対応可能。"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircle color="primary" fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="収納付きベッド・ソファ"
                                        secondary="デッドスペースを有効活用し、衣類や雑貨の収納場所確保。"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircle color="primary" fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="壁面収納・吊り下げラック"
                                        secondary="床面積を圧迫せず、整理整頓しやすい。"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircle color="primary" fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="共用家電は大容量・シンプルな操作性"
                                        secondary="みんなが使いやすいシンプル設計＆容量重視で選ぶのがコツ。"
                                    />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: '#e0f2f1', borderRadius: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                <ArrowRightAlt sx={{ verticalAlign: 'middle', mr: 1 }} />レイアウト・配置の工夫
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircle color="primary" fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="動線を意識した配置"
                                        secondary="人の行き来が多い場所は広く空ける。家具は壁沿いに置くと圧迫感が減少。"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircle color="primary" fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="共有収納スペースの明確化"
                                        secondary="棚やボックスで「誰のものか」分かる工夫を。"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircle color="primary" fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="毎月の整理・断捨離ルール"
                                        secondary="不要品の定期チェックで、スペースを常に快適に保つ。"
                                    />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>

                </Box>
            </Box>


            {/* 耐久性と使いやすさ */}
            <Box id="durability-usability" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    耐久性と使いやすさを考える
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアの家具・家電は<b>長期間・複数人で使う</b>ため、<b>「壊れにくい」「誰でも簡単に使える」</b>ことが絶対条件。選び方のポイントをチェックしましょう。
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: '#fffde7', borderRadius: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                <DoneAll color="success" sx={{ verticalAlign: 'middle', mr: 1 }} />耐久性のポイント
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="金属フレームや厚みのある天板"
                                        secondary="ソファ・テーブルは耐荷重や素材の厚みを確認。"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="有名メーカー・保証付き商品"
                                        secondary="家電は修理対応や部品供給が安心なブランドを。"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="丸洗い・分解掃除ができる"
                                        secondary="布製品やキッチン用品は、清潔に保ちやすい構造を重視。"
                                    />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                <DoneAll color="success" sx={{ verticalAlign: 'middle', mr: 1 }} />使いやすさのポイント
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircle color="primary" fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="シンプル操作・表示"
                                        secondary="ボタンやメニューが直感的で、機械が苦手な人にも優しい設計。"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircle color="primary" fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="メンテナンスしやすい"
                                        secondary="掃除やフィルター交換が簡単なものを選ぶと長持ち。"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircle color="primary" fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="怪我・事故防止の安全設計"
                                        secondary="角の丸い家具や転倒防止機能付き家電は、安心して共同利用できる。"
                                    />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>


            {/* コスト分担の方法 */}
            <Box id="cost-sharing" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    コスト分担のおすすめ方法
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    家具・家電の購入時、よくあるのが<b>「誰がいくら負担するか」</b>問題。後々のトラブルを防ぐためにも、最初に<b>明確なルール</b>を決めましょう。
                </Typography>
                <Paper elevation={0} sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2, mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                        <AttachMoney color="primary" sx={{ verticalAlign: 'middle', mr: 1 }} />コスト分担パターン
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><CheckCircle color="primary" fontSize="small" /></ListItemIcon>
                            <ListItemText
                                primary="完全均等割り"
                                secondary="共用アイテムは人数で割る。最もシンプルで納得感が高い。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircle color="primary" fontSize="small" /></ListItemIcon>
                            <ListItemText
                                primary="利用頻度による変動制"
                                secondary="「使う人だけ」や「部屋ごとに負担割合を変える」など、利用状況で調整。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircle color="primary" fontSize="small" /></ListItemIcon>
                            <ListItemText
                                primary="オーナー制"
                                secondary="誰か一人が購入し、他の人が「レンタル費」を毎月支払う形式。"
                            />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    <b>【実践アドバイス】</b> 購入前に「分担方法」と「支払い記録」をLINEグループやクラウドサービス（Googleスプレッドシート等）で残しておくと、後からのトラブルを防げます。
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>

                </Box>
            </Box>


            {/* 所有権の明確化 */}
            <Box id="ownership" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    所有権の明確化とトラブル防止策
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    <b>「引っ越す時、この冷蔵庫は誰のもの？」</b>という疑問が共同生活ではつきもの。<b>所有権</b>を曖昧にすると、退去や入居時に揉める原因になります。
                </Typography>
                <Paper elevation={0} sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                        <AssignmentInd color="primary" sx={{ verticalAlign: 'middle', mr: 1 }} />所有権明確化の工夫
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><CheckCircle color="primary" fontSize="small" /></ListItemIcon>
                            <ListItemText
                                primary="購入時に「オーナー名簿」を作る"
                                secondary="誰がいくら払ったか、主要アイテムごとに記録。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircle color="primary" fontSize="small" /></ListItemIcon>
                            <ListItemText
                                primary="共用・個人のラベリング"
                                secondary="家電や収納に「共用」「○○所有」など明記すると誤解が減る。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircle color="primary" fontSize="small" /></ListItemIcon>
                            <ListItemText
                                primary="退去・入居時の取り決め"
                                secondary="「退去時は折半で売却」「新メンバーと持ち分を引き継ぐ」など、事前ルール化が安心。"
                            />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body2" sx={{ mt: 2 }}>
                    <b>【専門的な視点から】</b> ルームシェア初期に「所有権ルール」を明文化しておくことで、円満な共同生活が長続きします。
                </Typography>
            </Box>


            {/* まとめ */}
            <Box id="conclusion" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめと快適ルームシェアのヒント
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェア生活を成功させる秘訣は、<b>「話し合い」「工夫」「記録」</b>の3つ。家具・家電の選び方や管理ルールをしっかり決めておけば、トラブルを防ぎ、互いに心地よい空間を築けます。
                </Typography>
                <List dense>
                    <ListItem>
                        <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                        <ListItemText primary="最初に「必要なものリスト」を全員で共有" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                        <ListItemText primary="コスト・所有権のルールを明確化" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                        <ListItemText primary="定期的に見直しや整理を実施" />
                    </ListItem>
                </List>
                <Typography variant="body2" sx={{ mt: 2 }}>
                    家具や家電は単なる「モノ」ではなく、<span style={{ color: '#1976d2', fontWeight: 'bold' }}>みんなの快適な関係と毎日を支える基盤</span>。この記事を参考に、あなたのルームシェアが素晴らしいものになりますように！
                </Typography>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-furniture-appliance-20-items',
    slug: 'roomshare-furniture-appliance-20-items',
    title: 'ルームシェア向けの家具・家電選び：共同生活に最適なアイテム20選',
    excerpt: '必要な家具・家電リスト スペース効率の良い選び方 耐久性と使いやすさ コスト分担の方法 所有権の明確化',
    coverImage: '/images/blog/cover-roomshare-furniture_roomshare-furniture-appliance-20-items.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。大型ソファと観葉植物、温かみのあるウッド調インテリアが共同生活の快適さと安心感を伝えている',
    publishedAt: '2024-06-13T09:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', '住まい', 'ライフハック'],
    tags: ['家具選び', '家電', '共同生活', 'コスト分担', '所有権'],
    author: {
        name: '佐藤 由紀',
        avatar: '/images/avatars/yuki-sato.jpg',
        bio: '不動産業界で10年以上の実務経験を持ち、ルームシェアや共同生活に関する豊富な知識・アドバイスを提供。快適な住まい作りをサポートしています。',
        profilePicture: '/images/avatars/yuki-sato.jpg'
    },
    viewCount: 1054,
    contentComponent: PostContent
};

export default post;